import qs from 'qs';
import compact from '../../../../utils/compact';
import type { AgentApiProvidedJobPostsIndex } from '@/types/serializers';
import type { ProvidedJobPost, SearchQuery } from '../hooks/useProvidedJobPosts';
import { BULK_ACTION_MAX_COUNT } from '../constant';

/** index API の1ページあたりの件数（サーバ側のページングと一致させる） */
const PER_PAGE = 25;

/** 一括アクションの上限超過を表すエラー。呼び出し側で検索条件の絞り込みを促す表示に使う */
export class BulkActionLimitExceededError extends Error {}

/**
 * 全件選択モードの対象求人を解決する。
 * index API をページループで取得し、excludedIds を除外した求人を返す。
 * 対象件数が maxCount を超える場合は BulkActionLimitExceededError を投げる。
 *
 * 取得中に他ユーザーの更新で並び順が変わるとページ間で重複・取りこぼしが起きうるが、
 * 重複は ID で排除し、取りこぼしは実行時点のスナップショットとしてベストエフォートで許容する。
 */
export default async function fetchAllSelectedJobPosts({
  searchQuery,
  excludedIds,
  maxCount = BULK_ACTION_MAX_COUNT,
}: {
  searchQuery: SearchQuery;
  excludedIds: Set<number>;
  maxCount?: number;
}): Promise<ProvidedJobPost[]> {
  const query = qs.stringify({ search_query: compact(searchQuery) }, { arrayFormat: 'brackets' });
  const results = new Map<number, ProvidedJobPost>();

  // 除外は読み込み済みレコードにしか付かないため、このページ数で必ず全対象を走査できる（暴走防止の保険）
  const maxPages = Math.ceil((maxCount + 1 + excludedIds.size) / PER_PAGE);

  for (let page = 1; page <= maxPages; page++) {
    const response = await fetch(`/agent/api/provided_job_posts.json?${query}&page=${page}`);
    if (!response.ok) {
      throw new Error(`求人の取得に失敗しました (status: ${response.status})`);
    }
    const data: AgentApiProvidedJobPostsIndex = await response.json();

    // サーバの最新 total_count で判定する（全件選択後にレコードが増えたケースも捕捉）
    if (page === 1 && data.total_count - excludedIds.size > maxCount) {
      throw new BulkActionLimitExceededError();
    }

    for (const record of data.records) {
      if (excludedIds.has(record.id)) continue;
      results.set(record.id, record);
    }

    // 取得中に対象が増えた場合の二段目の防波堤
    if (results.size > maxCount) {
      throw new BulkActionLimitExceededError();
    }

    // 空ページは異常系の無限ループ防止として終端扱いにする
    if (data.is_last_page || data.records.length === 0) {
      return Array.from(results.values());
    }
  }

  // maxPages まで到達しても終端に達しなかった場合、部分結果を黙って返さない
  throw new Error('求人の取得が完了しませんでした');
}
